/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */
import { css, html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles';

/**
 * @prop {Boolean} condensed - Enables condensed mode.
 * @prop {String} role - *hide
 * @slot - Main slot for navigation items. Takes tabs as children.
 * @slot functions - Holds page functions such as Save, Export, Delete, etc. Takes icons as children.
 */
export class SHNavBar extends LitElement {
  static get properties() {
    return {
      role: { type: String, reflect: true },
      condensed: { type: Boolean, reflect: true },
    };
  }
  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          background-color: var(--base-2);
          transition: var(--transition-time) all ease-in-out;
          box-shadow: var(--shadow-navigation);
          z-index: 3;
          width: 100%;
          overflow: auto;
          display: block;
          flex-shrink: 0;
        }

        :host([slot='footer']) .tabs-wrapper {
          flex: none;
          margin: auto;
        }

        .nav-bar-wrapper {
          height: 3.5rem;
          width: 100%;
          padding: 0 1rem;
          box-sizing: border-box;
          display: flex;
          flex-direction: row;
        }

        .tabs-wrapper {
          flex: 1;
          display: flex;
          overflow: hidden;
          padding: 1rem 0rem;
        }

        .tabs-wrapper > ::slotted(sh-tabs) {
          margin: -1rem 0;
        }

        .functions-wrapper {
          display: flex;
          flex-direction: row;
          align-items: center;
        }

        .functions-wrapper > ::slotted(*[slot='functions']) {
          margin-left: 0.5rem;
        }
        :host([condensed]) .nav-bar-wrapper {
          height: 2.5rem;
        }
      `,
    ];
  }
  render() {
    return html`<div class="nav-bar-wrapper">
      <div class="tabs-wrapper">
        <slot id="tabs" @slotchange=${() => this.tabsSlotUpdated()}></slot>
      </div>
      <div class="functions-wrapper">
        <slot name="functions" id="functions"></slot>
      </div>
    </div> `;
  }

  firstUpdated() {
    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'button');
    }
  }

  tabsSlotUpdated() {
    const tabs = this.querySelectorAll('sh-tabs');
    // trigger nav prop of tabs
    if (this.slot === 'nav') {
      for (let i = 0; i < tabs.length; i++) {
        tabs[i]._navContext = true;
      }
    }
    // trigger footer prop of tabs
    if (this.slot === 'footer') {
      for (let i = 0; i < tabs.length; i++) {
        tabs[i]._footerContext = true;
      }
    }
    this._handleCondensed();
  }

  updated(changedProperties) {
    if (changedProperties.has('condensed')) {
      this._handleCondensed();
    }
  }

  _handleCondensed() {
    const tabItems = this.querySelectorAll('sh-tab-item');
    const tabs = this.querySelectorAll('sh-tabs');
    tabs.forEach((el) => {
      el.condensed = this.condensed;
    });
    tabItems.forEach((el) => {
      el.condensed = this.condensed;
    });
  }
}
customElements.define('sh-nav-bar', SHNavBar);
